/** Advance only at a scene boundary; uploads and snapshot polling must not interrupt a hold. */
export function selectScene<T extends { id: string }>(
  photos: T[],
  previousHero: string | undefined,
  previousNewest: string | undefined,
  count: number,
  featured: boolean,
): T[] {
  const newestFirst = [...photos].reverse();
  if (!newestFirst.length) return [];
  const previousIndex = newestFirst.findIndex(
    (photo) => photo.id === previousHero,
  );
  const start =
    previousNewest !== newestFirst[0].id || previousIndex < 0
      ? 0
      : (previousIndex + (featured ? 1 : count)) % newestFirst.length;
  return Array.from(
    { length: Math.min(count, newestFirst.length) },
    (_, index) => newestFirst[(start + index) % newestFirst.length],
  );
}

export function sceneInterval(
  duration: number,
  transitionMs: number,
  transition: string,
) {
  return duration * 1000 + (transition === "cut" ? 0 : transitionMs);
}

/** Percent coordinates keep the same composition at custom LED resolutions. */
export function featuredFrame(
  index: number,
  total: number,
  filmstrip: boolean,
  portrait: boolean,
) {
  if (total === 1) return { left: 0, top: 0, width: 100, height: 100 };
  const strip = filmstrip || portrait;
  if (index === 0)
    return strip
      ? { left: 12, top: 1, width: 76, height: total > 1 ? 70 : 98 }
      : { left: 25, top: 5, width: 50, height: 90 };
  const satellites = total - 1;
  if (strip) {
    const gap = 1.5;
    const width = Math.min(18, (96 - gap * (satellites - 1)) / satellites);
    const span = width * satellites + gap * (satellites - 1);
    return {
      left: (100 - span) / 2 + (index - 1) * (width + gap),
      top: 78,
      width,
      height: 21,
    };
  }
  const slot = index - 1;
  const right = slot % 2 === 1;
  const rows = Math.ceil(satellites / 2);
  const height = Math.min(30, 86 / rows - 4);
  const row = Math.floor(slot / 2);
  const stride = height + 5;
  const top = (100 - (rows * stride - 5)) / 2 + row * stride;
  const width = row % 2 === 0 ? 19 : 17;
  return { left: right ? 100 - width - 2 : 2, top, width, height };
}

/** Keep all unaffected slots unchanged; featured scenes promote exactly one satellite. */
export function advanceTiles<T extends { id: string }>(
  photos: T[],
  current: T[],
  count: number,
  featured: boolean,
  turn: number,
): T[] {
  if (!photos.length) return [];
  const available = new Map(photos.map((p) => [p.id, p]));
  const next = current
    .filter((p) => available.has(p.id))
    .slice(0, count)
    .map((p) => available.get(p.id)!);
  const unused = () => {
    const list = [...photos]
      .reverse()
      .filter((p) => !next.some((n) => n.id === p.id));
    return list[Math.max(0, turn - 1) % list.length];
  };
  while (next.length < Math.min(count, photos.length)) {
    const p = unused();
    if (!p) break;
    next.push(p);
  }
  if (!current.length) return next;
  if (featured && next.length > 1) {
    const slot = 1 + (Math.max(0, turn - 1) % (next.length - 1));
    const outgoing = next[0];
    const fresh = unused();
    next[0] = next[slot];
    next[slot] = fresh || outgoing;
  } else {
    const slot = Math.max(0, turn - 1) % next.length;
    const fresh = unused();
    if (fresh) next[slot] = fresh;
  }
  return next;
}
